/**
 * Page d'un article SATELLITE (route : /apprendre/:parentSlug/:satelliteSlug).
 *
 * Articles courts (5-9 min) qui densifient les 9 dossiers magazine ETM.
 * Format : kicker + titre Playfair + body[] (paragraphes, sous-titres,
 * keypoints, callouts, citations, sources).
 */
import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Clock, BookOpen, Quote, AlertTriangle,
  Info, Lightbulb, X, CheckCircle2, Newspaper, Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  getSatelliteBySlug,
  getPublishedSatellitesByParent,
} from '../data/etmIssues/satellites';
import { getEtmIssueBySlug, ETM_ISSUES_BY_SLUG } from '../data/etmIssues';
import useSeo from '../hooks/useSeo';
import StickyMobileActionBar from '../components/StickyMobileActionBar';

const CALLOUT_STYLES = {
  info:    { icon: Info,           bg: 'bg-cyan-500/10 border-cyan-500/40 text-cyan-700 dark:text-cyan-300' },
  warning: { icon: AlertTriangle,  bg: 'bg-amber-500/10 border-amber-500/40 text-amber-700 dark:text-amber-300' },
  tip:     { icon: Lightbulb,      bg: 'bg-emerald-500/10 border-emerald-500/40 text-emerald-700 dark:text-emerald-300' },
  mistake: { icon: X,              bg: 'bg-rose-500/10 border-rose-500/40 text-rose-700 dark:text-rose-300' },
};

// Convertit un texte en slug pour ancres TOC (« 1. Quels véhicules concernés ? » → « quels-vehicules-concernes »)
const toSlug = (str) =>
  (str || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // accents
    .replace(/^\d+\.\s*/, '')                          // numérotation au début
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const RenderBlock = ({ block }) => {
  if (block.type === 'paragraph') {
    return (
      <p
        className="text-base sm:text-lg leading-relaxed mb-5 text-foreground/90"
        dangerouslySetInnerHTML={{ __html: block.text }}
      />
    );
  }
  if (block.type === 'subtitle') {
    return (
      <h2
        id={toSlug(block.text)}
        className="text-xl sm:text-2xl font-black mt-10 mb-4 leading-tight scroll-mt-20"
        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
      >
        {block.text}
      </h2>
    );
  }
  if (block.type === 'callout') {
    const meta = CALLOUT_STYLES[block.tone] || CALLOUT_STYLES.info;
    const Icon = meta.icon;
    return (
      <div className={`my-6 p-4 sm:p-5 rounded-xl border-l-4 ${meta.bg}`}>
        <div className="flex items-start gap-3">
          <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-black text-sm mb-1">{block.title}</div>
            <div className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: block.text }} />
          </div>
        </div>
      </div>
    );
  }
  if (block.type === 'keypoints') {
    return (
      <div className="my-6 p-5 rounded-xl bg-muted/30 border border-border">
        <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-3">
          {block.title || 'À retenir'}
        </div>
        <ul className="space-y-2">
          {(block.items || []).map((item, i) => (
            <li key={i} className="flex gap-2 text-sm leading-relaxed">
              <CheckCircle2 className="w-4 h-4 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
              <span dangerouslySetInnerHTML={{ __html: item }} />
            </li>
          ))}
        </ul>
      </div>
    );
  }
  if (block.type === 'quote') {
    return (
      <blockquote className="my-8 p-5 sm:p-6 rounded-xl bg-[hsl(var(--primary))]/5 border-l-4 border-[hsl(var(--primary))]">
        <Quote className="w-6 h-6 text-[hsl(var(--primary))] opacity-30 mb-2" />
        <p className="text-base sm:text-lg italic font-semibold leading-relaxed mb-2">« {block.text} »</p>
        {block.source && (
          <cite className="block text-xs not-italic text-muted-foreground">— {block.source}</cite>
        )}
      </blockquote>
    );
  }
  if (block.type === 'sources') {
    return (
      <div className="mt-10 pt-6 border-t border-border">
        <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-3">
          Sources et références
        </div>
        <ul className="space-y-1.5 text-xs text-muted-foreground">
          {(block.items || []).map((s, i) => (
            <li key={i} className="leading-relaxed">
              {s.url ? (
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:text-[hsl(var(--primary))] underline-offset-2 hover:underline">
                  {s.label}
                </a>
              ) : (
                s.label
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return null;
};

export default function EtmSatellitePage() {
  const { parentSlug, satelliteSlug } = useParams();
  const navigate = useNavigate();
  const article = getSatelliteBySlug(satelliteSlug);
  const parent = ETM_ISSUES_BY_SLUG[parentSlug];
  const otherSatellites = getPublishedSatellitesByParent(parentSlug).filter(
    (a) => a.slug !== satelliteSlug,
  );

  useSeo({
    title: article ? `${article.title} · Code 2 Roues` : 'Article — Code 2 Roues',
    description: article?.teaser || '',
    path: `/apprendre/${parentSlug}/${satelliteSlug}`,
  });

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [satelliteSlug]);

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="satellite-not-found">
        <h1 className="text-2xl font-black mb-3">Article introuvable</h1>
        <p className="text-muted-foreground mb-6">Cet article n'existe pas ou a été déplacé.</p>
        <Button onClick={() => navigate(`/apprendre/${parentSlug || ''}`)}>
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Retour au dossier
        </Button>
      </div>
    );
  }

  if (article.status !== 'published') {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="satellite-coming-soon">
        <Sparkles className="w-10 h-10 text-[hsl(var(--primary))] mx-auto mb-4" />
        <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2">
          {article.kicker} · En cours de rédaction
        </div>
        <h1
          className="text-3xl sm:text-4xl font-black mb-4 leading-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {article.title}
        </h1>
        <p className="text-base text-muted-foreground mb-6">{article.teaser}</p>
        <p className="text-sm text-muted-foreground italic mb-6">
          Article en cours de rédaction. Revenez bientôt — ou explorez les autres articles déjà publiés du dossier.
        </p>
        <Button asChild>
          <Link to={`/apprendre/${parentSlug}`}>
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Retour au dossier {parent?.shortTitle || ''}
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 pb-32 lg:pb-12" data-testid="satellite-page">
      {/* Fil d'ariane */}
      <nav className="mb-6 text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
        <Link to="/revision" className="hover:text-foreground">Magazine</Link>
        <span>›</span>
        <Link to={`/apprendre/${parentSlug}`} className="hover:text-foreground">{article.parentTitle}</Link>
        <span>›</span>
        <span className="text-foreground/70">{article.title}</span>
      </nav>

      {/* HEADER */}
      <header className="mb-8">
        <div className="inline-flex items-center gap-2 mb-3 text-[10px] font-black uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
          <Newspaper className="w-3.5 h-3.5" />
          <span>{article.kicker}</span>
        </div>
        <h1
          className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] mb-4"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          data-testid="satellite-title"
        >
          {article.title}
        </h1>
        <p className="text-lg sm:text-xl text-foreground/75 leading-relaxed">{article.teaser}</p>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> {article.readTimeMin} min de lecture
          </span>
          {article.publishedAt && (
            <>
              <span className="opacity-50">·</span>
              <span>Publié le {new Date(article.publishedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            </>
          )}
        </div>
      </header>

      {/* SOMMAIRE — généré automatiquement à partir des subtitles du body.
          Affiché uniquement si l'article a 3+ sous-sections (sinon inutile). */}
      {(() => {
        const tocItems = (article.body || [])
          .filter((b) => b.type === 'subtitle')
          .map((b) => ({ text: b.text, slug: toSlug(b.text) }));
        if (tocItems.length < 3) return null;
        return (
          <nav
            className="mb-8 rounded-xl border-2 border-border bg-muted/20 p-4 sm:p-5"
            aria-label="Sommaire de l'article"
            data-testid="article-toc"
          >
            <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5" /> Sommaire · {tocItems.length} sections
            </p>
            <ol className="space-y-1.5 list-none">
              {tocItems.map((it, idx) => (
                <li key={it.slug}>
                  <a
                    href={`#${it.slug}`}
                    className="text-sm leading-snug hover:text-primary hover:underline flex gap-2"
                  >
                    <span className="text-muted-foreground font-bold tabular-nums">{idx + 1}.</span>
                    <span>{it.text.replace(/^\d+\.\s*/, '')}</span>
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        );
      })()}

      {/* BODY */}
      <div className="prose-magazine">
        {(article.body || []).map((block, i) => (
          <RenderBlock key={i} block={block} />
        ))}
      </div>

      {/* AUTRES ARTICLES DU MÊME DOSSIER */}
      {otherSatellites.length > 0 && (
        <section className="mt-12 pt-8 border-t border-border">
          <h2
            className="text-xl font-black mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Autres articles du dossier {article.parentTitle}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {otherSatellites.slice(0, 4).map((a) => (
              <Link
                key={a.slug}
                to={`/apprendre/${parentSlug}/${a.slug}`}
                className="group block"
                data-testid={`satellite-related-${a.slug}`}
              >
                <Card className="h-full hover:border-[hsl(var(--primary))]/60 transition-colors">
                  <CardContent className="p-4">
                    <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">
                      {a.kicker}
                    </div>
                    <h3
                      className="text-sm font-black leading-tight mb-2 group-hover:text-[hsl(var(--primary))] transition-colors"
                      style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                      {a.title}
                    </h3>
                    <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {a.readTimeMin} min
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CTA RETOUR AU DOSSIER */}
      <div className="mt-12 p-6 rounded-2xl bg-muted/30 border border-border text-center">
        <BookOpen className="w-8 h-8 text-[hsl(var(--primary))] mx-auto mb-3" />
        <h3 className="text-base font-black mb-1">Continuer le dossier {article.parentTitle}</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Reprenez la lecture du dossier-pilier ou explorez d'autres thématiques ETM.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <Button asChild variant="outline">
            <Link to={`/apprendre/${parentSlug}`}>
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Retour au dossier
            </Link>
          </Button>
          <Button asChild>
            <Link to="/revision">
              Le magazine complet <ArrowRight className="w-4 h-4 ml-1.5" />
            </Link>
          </Button>
        </div>
      </div>
      <StickyMobileActionBar
        backTo={`/apprendre/${parentSlug}`}
        backLabel="Dossier"
        cta={{ to: '/revision', label: 'Magazine', icon: Newspaper }}
      />
    </article>
  );
}
